// HealthProfileCockpit — the top-of-modal cockpit upgrade for the
// Health Client Profile. Drops in above the existing sections without
// removing them. Surfaces:
//
//   - DUPLICATE banner (when /api/profile/dedupe finds groups)
//   - AEP countdown (computed from earliest renewal_date)
//   - Talk Track (Anthropic-generated, cached server-side)
//   - Money-ranked top cross-sell ("Start here")
//
// All four are lazy-loaded; the modal still opens fast even when the
// AI endpoints are slow.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { AlertTriangle, Copy, Sparkles, Calendar, DollarSign, RefreshCcw, Check, CheckCircle2, Circle, Users, Wand2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(body?.error || `HTTP ${res.status}`);
    e.status = res.status; e.body = body;
    throw e;
  }
  return body;
}

// AEP runs Oct 15 → Dec 7. Mirror api/medicare/dashboard.js logic.
function aepStatus(today = new Date()) {
  const y = today.getUTCFullYear();
  const start = new Date(Date.UTC(y, 9, 15));
  const end   = new Date(Date.UTC(y, 11, 7));
  if (today < start) {
    return { phase: 'pre',    days: Math.ceil((start - today) / 86_400_000), label: `AEP opens in ${Math.ceil((start - today) / 86_400_000)} days` };
  }
  if (today <= end) {
    return { phase: 'active', days: Math.ceil((end - today) / 86_400_000),   label: `AEP active · ${Math.ceil((end - today) / 86_400_000)} days remaining` };
  }
  const nextStart = new Date(Date.UTC(y + 1, 9, 15));
  return { phase: 'post', days: Math.ceil((nextStart - today) / 86_400_000), label: `AEP ${y + 1} opens in ${Math.ceil((nextStart - today) / 86_400_000)} days` };
}

function daysUntil(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
}

export default function HealthProfileCockpit({ clientId, policies = [], onPoliciesChanged = () => {}, onGotoTab = null, showToast = () => {} }) {
  const [dupes,     setDupes]     = React.useState(null);
  const [crossSell, setCrossSell] = React.useState(null);
  const [talkTrack, setTalkTrack] = React.useState(null);
  const [enrich,    setEnrich]    = React.useState(null);
  const [plan,      setPlan]      = React.useState(null);
  const [busy,      setBusy]      = React.useState(false);
  const [generating, setGenerating] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!clientId) return;
    try {
      const [d, c, t, e, p] = await Promise.all([
        apiFetch(`/api/profile/dedupe?client_id=${encodeURIComponent(clientId)}`).catch(() => null),
        apiFetch(`/api/profile/cross-sell?client_id=${encodeURIComponent(clientId)}`).catch(() => null),
        apiFetch(`/api/profile/talk-track?client_id=${encodeURIComponent(clientId)}`).catch(() => null),
        apiFetch(`/api/profile/enrich?client_id=${encodeURIComponent(clientId)}`).catch(() => null),
        apiFetch(`/api/profile/sequenced-plan?client_id=${encodeURIComponent(clientId)}`).catch(() => null),
      ]);
      setDupes(d);
      setCrossSell(c);
      setTalkTrack(t);
      setEnrich(e);
      setPlan(p);
    } catch (err) {
      // Silently degrade — cockpit shouldn't block the modal from rendering.
      // eslint-disable-next-line no-console
      console.warn('[HealthProfileCockpit] load partial failure:', err.message || err);
    }
  }, [clientId]);

  React.useEffect(() => { load(); }, [load]);

  const mergeGroup = async (group) => {
    if (busy) return;
    const winner = group.rows[0]; // most-complete row (server pre-sorted)
    const losers = group.rows.slice(1);
    const winnerLabel = winner.plan_name || winner.member_id || winner.id.slice(0, 8);
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: `Merge ${losers.length} duplicate${losers.length === 1 ? '' : 's'} into "${winnerLabel}"?`,
      body: 'Their data is preserved and folded into the winner row.',
      confirmLabel: 'Merge duplicates',
    }) : Promise.resolve(window.confirm(`Merge ${losers.length} duplicate${losers.length === 1 ? '' : 's'} into ${winnerLabel}?`)));
    if (!ok) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/profile/dedupe', {
        method: 'POST',
        body: JSON.stringify({
          action: 'merge',
          winner_id: winner.id,
          loser_ids: losers.map(l => l.id),
        }),
      });
      showToast(`Merged ${r.loser_ids.length} policies · enriched ${r.fields_enriched.length} fields.`, 'success');
      await load();
      onPoliciesChanged();
    } catch (err) {
      showToast(`Merge failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const generateTalkTrack = async () => {
    if (generating || !clientId) return;
    setGenerating(true);
    try {
      const r = await apiFetch('/api/profile/talk-track', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId }),
      });
      setTalkTrack(r);
      showToast('Talk track ready.', 'success');
    } catch (err) {
      if (err.status === 402) {
        showToast('Out of credits — top up your AI Wallet to generate a talk track.', 'error');
      } else {
        showToast(`Talk-track generation failed: ${err.message}`, 'error');
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyTalkTrack = () => {
    if (!talkTrack?.brief) return;
    try {
      navigator.clipboard.writeText(talkTrack.brief);
      showToast('Copied.', 'success');
    } catch { /* clipboard blocked */ }
  };

  // Apply one auto-enrichment suggestion (the high-confidence path —
  // renewal_date derived from effective_date). Loops all auto-applyable
  // suggestions across all policies in one batch when no target passed.
  const applyEnrichments = async (target) => {
    if (busy || !enrich) return;
    const suggestions = (enrich.per_policy || []).flatMap(pp =>
      pp.suggestions
        .filter(s => s.confidence === 'high')
        .filter(s => !target || (target.policyId === pp.policy_id && target.field === s.field))
        .map(s => ({ policy_id: pp.policy_id, field: s.field, suggested: s.suggested }))
    );
    if (suggestions.length === 0) {
      showToast('No high-confidence enrichments available.', 'info');
      return;
    }
    setBusy(true);
    try {
      for (const s of suggestions) {
        await apiFetch('/api/profile/enrich', {
          method: 'POST',
          body: JSON.stringify({
            action:    'apply',
            policy_id: s.policy_id,
            fields:    { [s.field]: s.suggested },
          }),
        });
      }
      showToast(`Applied ${suggestions.length} enrichment${suggestions.length === 1 ? '' : 's'}.`, 'success');
      await load();
      onPoliciesChanged();
    } catch (err) {
      showToast(`Apply failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  // AEP clock — driven by earliest renewal_date on active policies,
  // falls back to calendar AEP window when no renewal date on file.
  const renewalDays = React.useMemo(() => {
    const dates = (policies || [])
      .filter(p => (p.status || 'active') === 'active' && !p.merged_into)
      .map(p => daysUntil(p.renewal_date))
      .filter(d => d != null && d >= 0)
      .sort((a, b) => a - b);
    return dates[0] ?? null;
  }, [policies]);
  const aep = aepStatus();

  const dupeGroups = dupes?.duplicate_groups || [];
  const topCrossSell = crossSell?.recommendations?.[0] || null;
  const household    = crossSell?.household_rollup || null;
  const enrichSummary = enrich?.summary || null;
  const autoApplyable = enrichSummary?.auto_applyable || 0;
  const planSteps     = plan?.steps || [];

  return (
    <div className="border-2 border-amber-700/30 rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-black to-slate-950 text-white">
      <div className="px-5 py-3 border-b border-amber-900/30 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400/80 flex items-center gap-2">
            <Sparkles size={11}/> AI Profile Cockpit
          </div>
          <div className="text-sm text-amber-100/80 mt-0.5">Top-of-profile triage — duplicates, money, AEP clock, talk track.</div>
        </div>
        <button
          onClick={load}
          disabled={busy}
          className="text-[10px] uppercase tracking-wider font-bold text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCcw size={10}/> Refresh
        </button>
      </div>

      {/* DUPLICATE banner — highest priority, full width when present. */}
      {dupeGroups.length > 0 && (
        <div className="px-5 py-3 bg-rose-950/60 border-b border-rose-800/40">
          {dupeGroups.map((g, idx) => {
            const memberId = g.rows[0].member_id;
            const carrier  = g.rows[0].carrier;
            return (
              <div key={idx} className="flex items-start justify-between gap-3 flex-wrap mb-2 last:mb-0">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-rose-100 flex items-center gap-2">
                    <AlertTriangle size={14}/> {g.rows.length} likely duplicates · {carrier || 'unknown carrier'}{memberId ? ` · member ID ${memberId}` : ''}
                  </div>
                  <div className="text-[11px] text-rose-200/70 mt-0.5">
                    {g.kind === 'strong' && 'STRONG match — same carrier, member ID, plan type.'}
                    {g.kind === 'medium' && 'MEDIUM match — same carrier + member ID, different plan type. Could be the same policy mis-classified.'}
                    {g.kind === 'soft'   && 'SOFT match — same carrier + plan type, no member ID. Likely a half-imported row.'}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => mergeGroup(g)}
                    disabled={busy}
                    className="text-xs font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 rounded disabled:opacity-50"
                  >
                    Merge
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-amber-900/20">
        {/* AEP / renewal clock */}
        <div className="bg-gradient-to-br from-slate-950 to-black px-4 py-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-400/70">
            <Calendar size={10}/> AEP / renewal clock
          </div>
          {renewalDays != null ? (
            <>
              <div className={`text-2xl font-black mt-2 tabular-nums ${renewalDays <= 60 ? 'text-amber-400' : 'text-white'}`}>
                {renewalDays}d
              </div>
              <div className="text-[10px] text-slate-400 mt-1">until earliest renewal · {aep.label}</div>
              {renewalDays <= 60 && (
                <div className="text-[10px] text-amber-300 mt-1 font-semibold">Re-shop window opening — plans + formularies change every year.</div>
              )}
            </>
          ) : (
            <>
              <div className="text-xl font-bold mt-2 text-white">{aep.label}</div>
              <div className="text-[10px] text-slate-400 mt-1">No renewal date on file — confirm before AEP.</div>
            </>
          )}
        </div>

        {/* Top cross-sell — "Start here" */}
        <div className="bg-gradient-to-br from-slate-950 to-black px-4 py-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-400/70">
            <DollarSign size={10}/> Top money gap
          </div>
          {topCrossSell ? (
            <>
              <div className="text-xl font-black mt-2 text-amber-400 flex items-center gap-2">
                {topCrossSell.label}
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-black px-1.5 py-0.5 rounded-full">Start here</span>
              </div>
              <div className="text-[11px] text-slate-300 mt-1">
                est. ${topCrossSell.est_premium_lo}–{topCrossSell.est_premium_hi}/mo · ${topCrossSell.est_commission_mo}/mo commission · {topCrossSell.close_ease}
              </div>
              <div className="text-[10px] text-amber-300/80 mt-1.5 italic leading-snug">{topCrossSell.why_now}</div>
            </>
          ) : (
            <div className="text-xs text-slate-400 mt-2">No gaps to offer — client's stack is full.</div>
          )}
        </div>

        {/* Talk track */}
        <div className="bg-gradient-to-br from-slate-950 to-black px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-400/70">
              <Sparkles size={10}/> Talk track
            </div>
            <div className="flex items-center gap-2">
              {talkTrack?.brief && (
                <button
                  onClick={copyTalkTrack}
                  title="Copy to clipboard"
                  className="text-amber-300/70 hover:text-amber-200"
                >
                  <Copy size={11}/>
                </button>
              )}
              <button
                onClick={generateTalkTrack}
                disabled={generating}
                className="text-[10px] uppercase tracking-wider font-bold text-amber-300 hover:text-amber-200 disabled:opacity-50"
              >
                {generating ? '…' : talkTrack?.brief ? 'Regenerate' : 'Generate'}
              </button>
            </div>
          </div>
          {talkTrack?.brief ? (
            <>
              <div className="text-[11px] text-white mt-2 leading-relaxed whitespace-pre-wrap">{talkTrack.brief}</div>
              {talkTrack.cached_at && (
                <div className="text-[9px] text-slate-500 mt-1.5">cached {new Date(talkTrack.cached_at).toLocaleString()}</div>
              )}
            </>
          ) : (
            <div className="text-[11px] text-slate-400 mt-2 leading-snug italic">
              Click Generate for a 2-3 line call brief grounded in this client's real data.
            </div>
          )}
        </div>
      </div>

      {/* SEQUENCED PLAY — the ordered checklist. Replaces flat
          "Next Agent Actions" by ordering steps by leverage. */}
      {planSteps.length > 0 && (
        <div className="border-t border-amber-900/30 px-5 py-3 bg-slate-950/60">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400/80">
              The play — top to bottom
            </div>
            <div className="text-[10px] text-amber-300/60">
              {planSteps.filter(s => s.status === 'done').length} / {planSteps.length} done
            </div>
          </div>
          <ol className="space-y-1.5">
            {planSteps.map((s, idx) => {
              const isDone   = s.status === 'done';
              const isActive = s.status === 'active';
              const isNextActive = isActive && plan?.next_active_step === s.key;
              const isClickable = !isDone && s.action_kind === 'navigate' && s.target_tab && onGotoTab;
              const handleClick = () => {
                if (!isClickable) return;
                onGotoTab(s.target_tab);
                // The profile modal owns close-on-navigation. The
                // tab change won't unmount this modal automatically;
                // fire a global event so the modal can close itself.
                try {
                  window.dispatchEvent(new CustomEvent('ct:close-health-profile-modal'));
                } catch {}
              };
              return (
                <li
                  key={s.key}
                  onClick={handleClick}
                  className={`flex items-start gap-2 px-2.5 py-1.5 rounded transition-colors ${
                    isNextActive ? 'bg-amber-500/15 border border-amber-500/40' : 'bg-slate-900/40'
                  } ${isClickable ? 'cursor-pointer hover:bg-amber-500/20' : ''}`}
                >
                  <span className="shrink-0 mt-0.5">
                    {isDone
                      ? <CheckCircle2 size={12} className="text-emerald-400"/>
                      : <Circle      size={12} className={isNextActive ? 'text-amber-400' : 'text-slate-600'}/>}
                  </span>
                  <span className="text-[10px] tabular-nums w-4 text-amber-400/40 shrink-0">{idx + 1}.</span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[12px] font-semibold ${isDone ? 'text-slate-400 line-through' : 'text-white'}`}>
                      {s.title}
                    </div>
                    {!isDone && (
                      <div className="text-[10px] text-amber-200/60 mt-0.5 leading-snug">{s.why}</div>
                    )}
                    {!isDone && s.cta && (
                      <div className={`text-[10px] mt-0.5 italic ${isClickable ? 'text-amber-300 underline' : 'text-amber-300'}`}>
                        → {s.cta}
                      </div>
                    )}
                  </div>
                  {!isDone && (
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border shrink-0 ${
                      s.action_kind === 'auto'     ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30' :
                      s.action_kind === 'navigate' ? 'bg-sky-500/20     text-sky-200     border-sky-400/30' :
                                                     'bg-slate-500/20   text-slate-300   border-slate-400/30'
                    }`}>
                      {s.action_kind}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* ENRICH SUGGESTIONS — only renders when there's at least one
          high-confidence auto-applyable suggestion OR a need-import /
          need-statement flag. Auto-applyable get an inline Apply All
          button; others get a "what you'd need" hint. */}
      {enrichSummary && enrichSummary.total_suggestions > 0 && (
        <div className="border-t border-amber-900/30 px-5 py-3 bg-slate-950/60">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400/80 flex items-center gap-1.5">
              <Wand2 size={11}/> Enrich missing data
            </div>
            {autoApplyable > 0 && (
              <button
                onClick={() => applyEnrichments()}
                disabled={busy}
                className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-400 text-black px-2.5 py-1 rounded disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Check size={10}/> Apply {autoApplyable} auto-fill{autoApplyable === 1 ? '' : 's'}
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <EnrichTile label="Auto-applyable" value={enrichSummary.auto_applyable || 0} tone="emerald"
              note="Derived from existing data — one click."/>
            <EnrichTile label="Need re-import" value={enrichSummary.requires_import || 0} tone="amber"
              note="Premium / subsidy / effective — re-import HealthSherpa CSV."/>
            <EnrichTile label="Need statement" value={enrichSummary.requires_statement || 0} tone="sky"
              note="Commission — import the carrier statement."/>
          </div>
        </div>
      )}

      {/* HOUSEHOLD ROLLUP — only when the client has 2+ household
          members. Shows total potential monthly commission across
          all open gaps for the whole household. */}
      {household && household.member_count > 1 && (
        <div className="border-t border-amber-900/30 px-5 py-3 bg-slate-950/60">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400/80 flex items-center gap-1.5">
              <Users size={11}/> Household rollup · {household.member_count} members
            </div>
            <div className="text-[10px] text-amber-300/60">
              {household.policies_in_household} policies
            </div>
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div>
              <div className="text-2xl font-black tabular-nums text-amber-400">
                ${household.total_potential_commission_mo.toFixed(0)}/mo
              </div>
              <div className="text-[10px] text-amber-200/70">
                estimated total commission if every household gap closes
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {Object.entries(household.gap_by_product || {})
                .filter(([, v]) => v.members_missing.length > 0)
                .map(([key, v]) => (
                  <span
                    key={key}
                    className="text-[10px] font-semibold uppercase tracking-wider bg-amber-900/30 text-amber-200 border border-amber-700/40 rounded-full px-2 py-0.5"
                    title={`${v.members_missing.length} household member${v.members_missing.length === 1 ? '' : 's'} missing ${v.label}`}
                  >
                    {v.label} · {v.members_missing.length}
                  </span>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EnrichTile({ label, value, tone, note }) {
  const tones = {
    emerald: 'bg-emerald-500/15 text-emerald-100 border-emerald-400/30',
    amber:   'bg-amber-500/15   text-amber-100   border-amber-400/30',
    sky:     'bg-sky-500/15     text-sky-100     border-sky-400/30',
  };
  return (
    <div className={`border rounded px-3 py-2 ${tones[tone]}`}>
      <div className="text-xl font-black tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-[10px] opacity-70 mt-0.5 leading-snug">{note}</div>
    </div>
  );
}
